import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  DEFAULT_PREVIEW_ATTRS,
  PREVIEW_ATTR_QUERY_KEYS,
  PreviewAttrsSchema,
  attrsToLinkQuery,
  queryToAttrs,
} from "./api";
import type { PreviewAttrs } from "./api";

const STORAGE_KEY = `healthForms.previewAttrs:${process.env.REACT_APP_API_URL_V2 ?? "default"}`;

const load = (): PreviewAttrs => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREVIEW_ATTRS;
    const result = PreviewAttrsSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : DEFAULT_PREVIEW_ATTRS;
  } catch {
    return DEFAULT_PREVIEW_ATTRS;
  }
};

const save = (attrs: PreviewAttrs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attrs));
  } catch {
    // ignore quota / disabled storage
  }
};

const withAttrs = (search: string, attrs: PreviewAttrs) => {
  const params = new URLSearchParams(search);
  PREVIEW_ATTR_QUERY_KEYS.forEach(key => params.delete(key));
  attrsToLinkQuery(attrs).forEach((value, key) => params.append(key, value));
  return params.toString();
};

export const usePersistedPreviewAttrs = () => {
  const history = useHistory();
  const { search } = useLocation();
  const attrs = React.useMemo(() => queryToAttrs(new URLSearchParams(search)) ?? load(), [search]);

  React.useEffect(() => {
    save(attrs);
    const next = withAttrs(history.location.search, attrs);
    if (next !== new URLSearchParams(history.location.search).toString()) {
      history.replace({ ...history.location, search: next });
    }
  }, [attrs, history]);

  const setAttrs = React.useCallback(
    (next: PreviewAttrs) => {
      save(next);
      history.replace({ ...history.location, search: withAttrs(history.location.search, next) });
    },
    [history]
  );

  return [attrs, setAttrs] as const;
};
